import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { api } from '../../lib/api.js';
import { useAuth } from '../../lib/AuthContext.jsx';
import NurseCard from '../../components/NurseCard.jsx';
import { Empty, Loading, useToast, Modal, SearchInput, PageHeader } from '../../components/ui.jsx';
import Icon from '../../components/Icon.jsx';
import { useDebounced } from '../../lib/hooks.js';
import { STATES, citiesOf, LANGUAGES } from '../../lib/locations.js';
import { todayISO, currency, formatDayLabel, formatTime } from '../../lib/format.js';

const SORTS = [
  ['rating', 'Top rated'],
  ['price_asc', 'Price: low to high'],
  ['price_desc', 'Price: high to low'],
  ['experience', 'Most experienced'],
  ['distance', 'Nearest first'],
];
const MAX_RATE = 1000;

/** Reads every filter out of the URL so searches are shareable and back-navigable. */
function useFilters() {
  const [params, setParams] = useSearchParams();
  const filters = useMemo(
    () => ({
      q: params.get('q') || '',
      serviceId: params.get('serviceId') || '',
      gender: params.get('gender') || '',
      maxRate: params.get('maxRate') || '',
      minExperience: params.get('minExperience') || '',
      minRating: params.get('minRating') || '',
      language: params.get('language') || '',
      state: params.get('state') || '',
      city: params.get('city') || '',
      date: params.get('date') || '',
      startTime: params.get('startTime') || '',
      endTime: params.get('endTime') || '',
      maxDistance: params.get('maxDistance') || '',
      sort: params.get('sort') || 'rating',
      page: Number(params.get('page') || 1),
    }),
    [params]
  );

  const update = useCallback(
    (patch, { resetPage = true } = {}) => {
      const next = new URLSearchParams(params);
      for (const [k, v] of Object.entries(patch)) {
        if (v === '' || v === null || v === undefined) next.delete(k);
        else next.set(k, v);
      }
      if (resetPage && !('page' in patch)) next.delete('page');
      setParams(next, { replace: true });
    },
    [params, setParams]
  );

  const clear = useCallback(() => setParams(new URLSearchParams(), { replace: true }), [setParams]);

  return [filters, update, clear];
}

/** One labelled block inside the filter panel. */
function FilterGroup({ label, icon, children }) {
  return (
    <div className="filter-group">
      <span className="label row-tight" style={{ gap: 6 }}>
        {icon && <Icon name={icon} size={13} />}
        {label}
      </span>
      {children}
    </div>
  );
}

/** Reusable chip row for the single-choice filters. */
function ChipRow({ options, value, onChange, ariaLabel }) {
  return (
    <div className="chips" role="group" aria-label={ariaLabel}>
      {options.map(([v, l]) => (
        <button key={v || 'any'} type="button" className="chip" aria-pressed={value === v} onClick={() => onChange(v)}>
          {l}
        </button>
      ))}
    </div>
  );
}

export default function Search() {
  const [filters, update, clear] = useFilters();
  const { user } = useAuth();
  const toast = useToast();

  const [services, setServices] = useState([]);
  const [data, setData] = useState({ results: [], total: 0, pages: 1 });
  const [loading, setLoading] = useState(true);
  const [coords, setCoords] = useState(null);
  const [showFilters, setShowFilters] = useState(false);
  const [text, setText] = useState(filters.q);

  useEffect(() => {
    api.get('/api/services').then(setServices).catch(() => {});
  }, []);

  // Search-as-you-type: the box updates the URL after a short pause, and the
  // URL is the single source of truth for the query below.
  const debouncedText = useDebounced(text, 400);
  useEffect(() => {
    if (debouncedText !== filters.q) update({ q: debouncedText });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedText]);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    api
      .get('/api/nurses', { ...filters, ...(coords || {}) })
      .then((res) => alive && setData(res))
      .catch((err) => alive && toast.error(err))
      .finally(() => alive && setLoading(false));
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters, coords]);

  const useMyLocation = () => {
    if (!navigator.geolocation) return toast.error('Location is not supported on this device.');
    navigator.geolocation.getCurrentPosition(
      (p) => {
        setCoords({ lat: p.coords.latitude, lng: p.coords.longitude });
        update({ sort: 'distance' });
        toast.success('Sorting by distance from your current location.');
      },
      () => toast.error('Could not read your location. Check browser permissions.'),
      { timeout: 8000 }
    );
  };

  const clearAll = () => {
    setText('');
    clear();
  };

  // Human-readable summary of what is currently narrowing the results, each
  // chip removable in one tap.
  const activeChips = [
    filters.q && { key: 'q', label: `“${filters.q}”`, clear: () => setText('') },
    filters.serviceId && {
      key: 'serviceId',
      label: services.find((s) => String(s.id) === filters.serviceId)?.name || 'Service',
    },
    filters.gender && { key: 'gender', label: `${filters.gender} nurse` },
    filters.maxRate && { key: 'maxRate', label: `up to ${currency(filters.maxRate)}/hr` },
    filters.date && {
      key: 'date',
      label:
        formatDayLabel(filters.date) +
        (filters.startTime && filters.endTime
          ? `, ${formatTime(filters.startTime)}–${formatTime(filters.endTime)}`
          : ''),
      also: ['startTime', 'endTime'],
    },
    filters.state && { key: 'state', label: filters.state, also: ['city'] },
    filters.city && { key: 'city', label: filters.city },
    filters.maxDistance && { key: 'maxDistance', label: `≤ ${filters.maxDistance} km` },
    filters.minExperience && { key: 'minExperience', label: `${filters.minExperience}+ yrs` },
    filters.minRating && { key: 'minRating', label: `${filters.minRating}★ +` },
    filters.language && { key: 'language', label: filters.language },
  ].filter(Boolean);

  const activeCount = activeChips.length;
  const hasOrigin = coords || user?.lat != null;

  /* ------------------------------------------------------ Filter panel */
  const panel = (
    <>
      <FilterGroup label="Service type" icon="stethoscope">
        <select value={filters.serviceId} onChange={(e) => update({ serviceId: e.target.value })} aria-label="Service type">
          <option value="">All services</option>
          {services.map((s) => (
            <option key={s.id} value={s.id}>
              {s.name}
            </option>
          ))}
        </select>
      </FilterGroup>

      <FilterGroup label="Nurse gender" icon="user">
        <ChipRow
          ariaLabel="Nurse gender"
          value={filters.gender}
          onChange={(gender) => update({ gender })}
          options={[
            ['', 'Any'],
            ['female', 'Female'],
            ['male', 'Male'],
          ]}
        />
      </FilterGroup>

      <FilterGroup label={`Budget · up to ${filters.maxRate ? currency(filters.maxRate) : 'any'}/hr`} icon="cash">
        <input
          type="range"
          min="200"
          max={MAX_RATE}
          step="25"
          value={filters.maxRate || MAX_RATE}
          aria-label="Maximum hourly rate"
          onChange={(e) => update({ maxRate: Number(e.target.value) === MAX_RATE ? '' : e.target.value })}
        />
        <div className="row-between tiny dim">
          <span>₹200</span>
          <span>₹{MAX_RATE}+</span>
        </div>
      </FilterGroup>

      <FilterGroup label="Available on" icon="calendar">
        <input
          type="date"
          value={filters.date}
          min={todayISO()}
          onChange={(e) => update({ date: e.target.value })}
          aria-label="Available on date"
        />
        {filters.date && (
          <div className="time-range mt-1">
            <input
              type="time"
              step="1800"
              value={filters.startTime}
              onChange={(e) => update({ startTime: e.target.value })}
              aria-label="From time"
            />
            <span className="time-arrow" aria-hidden="true">
              <Icon name="arrow-right" />
            </span>
            <input
              type="time"
              step="1800"
              value={filters.endTime}
              onChange={(e) => update({ endTime: e.target.value })}
              aria-label="To time"
            />
          </div>
        )}
      </FilterGroup>

      <FilterGroup label="Location" icon="map-pin">
        <select
          value={filters.state}
          onChange={(e) => {
            const state = e.target.value;
            // Reset city when it no longer belongs to the chosen state.
            const keepCity = filters.city && citiesOf(state).includes(filters.city);
            update({ state, city: keepCity ? filters.city : '' });
          }}
          aria-label="State"
        >
          <option value="">All states</option>
          {STATES.map((s) => (
            <option key={s} value={s}>
              {s}
            </option>
          ))}
        </select>
        <select
          value={filters.city}
          onChange={(e) => update({ city: e.target.value })}
          disabled={!filters.state}
          aria-label="City"
          style={{ marginTop: 8 }}
        >
          <option value="">{filters.state ? 'All cities' : 'Choose a state first'}</option>
          {citiesOf(filters.state).map((c) => (
            <option key={c} value={c}>
              {c}
            </option>
          ))}
        </select>
        <button className="btn btn-sm btn-ghost mt-1" onClick={useMyLocation}>
          <Icon name="navigation" /> Use my location
        </button>
        {hasOrigin && (
          <div className="mt-1">
            <ChipRow
              ariaLabel="Maximum distance"
              value={filters.maxDistance}
              onChange={(maxDistance) => update({ maxDistance })}
              options={[
                ['', 'Any distance'],
                ['5', '≤ 5 km'],
                ['10', '≤ 10 km'],
                ['25', '≤ 25 km'],
              ]}
            />
          </div>
        )}
      </FilterGroup>

      <FilterGroup label="Minimum experience" icon="briefcase">
        <ChipRow
          ariaLabel="Minimum experience"
          value={filters.minExperience}
          onChange={(minExperience) => update({ minExperience })}
          options={[
            ['', 'Any'],
            ['2', '2+ yrs'],
            ['5', '5+ yrs'],
            ['10', '10+ yrs'],
          ]}
        />
      </FilterGroup>

      <FilterGroup label="Minimum rating" icon="star">
        <ChipRow
          ariaLabel="Minimum rating"
          value={filters.minRating}
          onChange={(minRating) => update({ minRating })}
          options={[
            ['', 'Any'],
            ['3', '3★ +'],
            ['4', '4★ +'],
            ['4.5', '4.5★ +'],
          ]}
        />
      </FilterGroup>

      <FilterGroup label="Language spoken" icon="languages">
        <ChipRow
          ariaLabel="Language spoken"
          value={filters.language}
          onChange={(language) => update({ language })}
          options={[['', 'Any'], ...LANGUAGES.map((l) => [l, l])]}
        />
      </FilterGroup>
    </>
  );

  return (
    <>
      <PageHeader
        title="Find a nurse"
        subtitle={
          loading ? 'Searching…' : `${data.total} verified nurse${data.total === 1 ? '' : 's'} match your criteria`
        }
      />

      {/* ------------------------------------------------- Result toolbar */}
      <div className="result-bar" role="search">
        <SearchInput
          value={text}
          onChange={setText}
          placeholder="Search by name, speciality or qualification…"
          label="Search nurses"
        />
        <select
          value={filters.sort}
          onChange={(e) => update({ sort: e.target.value })}
          aria-label="Sort results"
          style={{ width: 'auto', flex: '0 1 190px' }}
        >
          {SORTS.map(([v, l]) => (
            <option key={v} value={v}>
              {l}
            </option>
          ))}
        </select>
        <button type="button" className="btn filters-toggle" onClick={() => setShowFilters(true)}>
          <Icon name="filter" />
          Filters
          {activeCount > 0 && <span className="pill pill-accent">{activeCount}</span>}
        </button>
      </div>

      {/* --------------------------------------------- Active filter chips */}
      {activeChips.length > 0 && (
        <div className="chips mb-2" aria-label="Active filters">
          {activeChips.map((c) => (
            <span key={c.key} className="chip chip-static chip-accent">
              {c.label}
              <button
                className="chip-remove"
                aria-label={`Remove ${c.label} filter`}
                onClick={() => {
                  c.clear?.();
                  update(Object.fromEntries([c.key, ...(c.also || [])].map((k) => [k, ''])));
                }}
              >
                <Icon name="close" />
              </button>
            </span>
          ))}
          <button className="chip" onClick={clearAll}>
            Clear all
          </button>
        </div>
      )}

      <div className="search-layout">
        {/* Sidebar on desktop; the same panel moves into a sheet on mobile. */}
        <div className="filters-col">
          <div className="card filters">
            <div className="card-header">
              <h3 className="grow">Filters</h3>
              <button className="btn btn-sm btn-ghost" onClick={clearAll} disabled={activeCount === 0}>
                Reset
              </button>
            </div>
            {panel}
          </div>
        </div>

        <div>
          {loading ? (
            <Loading rows={4} variant="card" />
          ) : data.results.length === 0 ? (
            <Empty
              art="no-results"
              title="No nurses match these filters"
              action={
                <button className="btn btn-primary" onClick={clearAll}>
                  Clear all filters
                </button>
              }
            >
              Try widening your budget, removing the date filter, or increasing the distance radius.
            </Empty>
          ) : (
            <>
              <div className="nurse-grid">
                {data.results.map((n, i) => (
                  <NurseCard key={n.id} nurse={n} index={i} />
                ))}
              </div>

              {data.pages > 1 && (
                <nav className="row mt-3" style={{ justifyContent: 'center' }} aria-label="Pagination">
                  <button
                    className="btn btn-sm"
                    disabled={filters.page <= 1}
                    onClick={() => update({ page: filters.page - 1 }, { resetPage: false })}
                  >
                    <Icon name="chevron-left" /> Previous
                  </button>
                  <span className="small muted tabular">
                    Page {filters.page} of {data.pages}
                  </span>
                  <button
                    className="btn btn-sm"
                    disabled={filters.page >= data.pages}
                    onClick={() => update({ page: filters.page + 1 }, { resetPage: false })}
                  >
                    Next <Icon name="chevron-right" />
                  </button>
                </nav>
              )}
            </>
          )}
        </div>
      </div>

      {/* ------------------------------------------- Mobile filter sheet */}
      {showFilters && (
        <Modal
          title="Filters"
          onClose={() => setShowFilters(false)}
          bodyClass="flush"
          footer={
            <>
              <button className="btn btn-ghost" onClick={clearAll} disabled={activeCount === 0}>
                <Icon name="refresh" /> Reset
              </button>
              <button className="btn btn-primary" onClick={() => setShowFilters(false)}>
                {loading ? 'Show results' : `Show ${data.total} nurse${data.total === 1 ? '' : 's'}`}
              </button>
            </>
          }
        >
          {panel}
        </Modal>
      )}
    </>
  );
}
